// The reads behind the rail's workspace menu (02-DESIGN/03-console/01-the-console.md
// § the shape: the workspace menu; hq decision 0069 point 5). The memberships
// are the identity plane's: read, where the sign-in is the platform's, on a
// connection to the plane that lasts for the read — asked for with `-` as the
// tenant, so one membership reaches the plane too (hq decision 0072), opened
// beside the tab's one connection, never held by the tab, and closed when the
// reply is in —
// when the menu first opens in a session and again on *All workspaces*. A
// child is a row of the parent's children list, which *Workspaces* reads.
// Nothing here keeps a second connection open.

import { Bus, Refusal, type Who } from "./bus.ts";
import type { PlatformProfile } from "./profile.ts";
import type { Session } from "../signin/tokens.ts";
import { planeSubject, PlaneVerb, Subjects, type Child, type ChildrenListReply } from "../wire/auth.ts";
import { SERVICE, type Membership, type MembershipsReply } from "../wire/identity.ts";

/** PlaneReader is what the one-shot read needs of a connection: the Bus, or a stand-in in tests. */
export interface PlaneReader {
  connect(): Promise<Who>;
  call<Rep>(service: string, subj: string, body?: unknown): Promise<Rep>;
  close(): Promise<void>;
}

/**
 * readMemberships opens a connection to the identity plane for one read, asked
 * for whatever the memberships, and closes it: the person's memberships. An abort — the menu went away, or
 * a pick was made mid-read — closes the connection at once and rejects.
 */
export async function readMemberships(
  origin: string,
  platform: PlatformProfile,
  lane: "oidc" | "github",
  open: () => PlaneReader = () => new Bus(origin, platform, null, lane, undefined, true),
  signal?: AbortSignal,
): Promise<Membership[]> {
  const check = () => {
    if (signal?.aborted === true) {
      throw new Refusal("aborted", "the read was given up");
    }
  };
  check();
  const conn = open();
  // An abort closes what is open now; the close below, once the connect has
  // answered, closes what a connect still dialling opens after it.
  const stop = () => {
    void conn.close().catch(() => undefined);
  };
  signal?.addEventListener("abort", stop, { once: true });
  try {
    const who = await conn.connect();
    check();
    const plane = who.plane;
    if (plane === undefined) {
      throw new Refusal("no-plane", "the sign-in asked for the identity plane and was placed elsewhere");
    }
    const reply = await conn.call<MembershipsReply>(
      SERVICE,
      planeSubject(plane.lane, plane.hash, PlaneVerb.memberships),
      undefined,
    );
    check();
    return reply.memberships;
  } finally {
    signal?.removeEventListener("abort", stop);
    await conn.close().catch(() => undefined);
  }
}

/** readChildren is the workspaces this one manages, on the tab's connection: the list *Workspaces* reads. */
export async function readChildren(bus: {
  call<Rep>(service: string, subj: string, body?: unknown): Promise<Rep>;
}): Promise<Child[]> {
  const reply = await bus.call<ChildrenListReply>(SERVICE, Subjects.childrenList(), undefined);
  return reply.children;
}

/**
 * The memberships read once per sign-in: kept for the page's life so the
 * menu reads them when it first opens in a session and not at every
 * workspace it moves to, and forgotten when the sign-in changes or on *All
 * workspaces*, which reads them again.
 */
let kept: { key: string; memberships: Membership[] } | undefined;

/** signInKey says which sign-in a session is: the same across a move between workspaces and a refresh. */
export function signInKey(s: Pick<Session, "lane" | "issuer" | "display">): string {
  return `${s.lane}|${s.issuer}|${s.display ?? ""}`;
}

/** keptMemberships answers the memberships read for this sign-in, if any. */
export function keptMemberships(session: Session): Membership[] | undefined {
  return kept !== undefined && kept.key === signInKey(session) ? kept.memberships : undefined;
}

/** keepMemberships keeps a read for this sign-in. */
export function keepMemberships(session: Session, memberships: Membership[]): void {
  kept = { key: signInKey(session), memberships };
}

/** forgetMemberships drops what was kept: a sign-out, another sign-in, *All workspaces*. */
export function forgetMemberships(): void {
  kept = undefined;
}
